import { AppBar, AppBarProps, Stack, Toolbar, Typography } from '@mui/material'
import { useWindowWidth } from '@react-hook/window-size'
import { forwardRef, ReactNode } from 'react'
import { IconButton } from 'utoniq-ui'

import { styles, styleSets } from '@/components/styles'
import { constants } from '@/config'
import { useRouter } from '@/core'

import { defaultTheme } from '../styles/themes/defaultTheme'

export type HeaderProps = {
  back?: boolean
  defaultBackPage?: string
  title?: ReactNode
  leftTitle?: boolean
  right?: ReactNode
  left?: ReactNode
  type?: 'normal' | 'modal'
  color?: string
  bgcolor?: string
} & Omit<AppBarProps, 'title' | 'color'>

export const ConsoleHeader = forwardRef<HTMLElement, HeaderProps>(
  (
    {
      back = false,
      defaultBackPage,
      type,
      left,
      title,
      leftTitle,
      color,
      right,
      position = 'fixed',
      bgcolor,
      ...props
    },
    ref
  ) => {
    const { router } = useRouter()
    const width = useWindowWidth()
    const desktopSideMenuWidth = constants.ui.layout.desktopSideMenuWidth

    return (
      <AppBar
        position={position}
        {...props}
        sx={{
          width: {
            xs: '100%',
            // PC 表示の場合はサイドメニューの幅分幅を小さくする
            md: `calc(100% - ${desktopSideMenuWidth}px)`,
          },
          minWidth: 0,
          margin: '0 auto',
          pt: styles.safeAreaTop,
          left: {
            xs: 0,
            // PC 表示の場合はサイドメニューの幅分左に間隔をとる
            md: `${desktopSideMenuWidth}px`,
          },
          boxShadow: 0,
          backgroundColor: bgcolor || 'background.default',
          // override property backgroundImage of Material-ui to display as designed
          backgroundImage: 'none',
          zIndex: 10,
          ...(props.sx || {}),
        }}
        ref={ref}
      >
        <Toolbar
          disableGutters
          sx={theme => ({
            margin: '0 auto',
            width: {
              xs: 'calc(100% - 40px)',
              lg: width - desktopSideMenuWidth - 40,
            },
            maxWidth: { lg: defaultTheme.breakpoints?.values?.lg ?? 1440 },
            color: 'text.primary',
            minHeight: 56,
            [theme.breakpoints.up('sm')]: {
              minHeight: 56,
            },
          })}
        >
          <Stack flex={1} direction="row" justifyContent="flex-start">
            {back &&
              (type === 'modal' ? (
                <IconButton.Close
                  onClick={() => router?.goBack(defaultBackPage)}
                  size="medium"
                  sx={{
                    ml: '-8px',
                  }}
                  color={color}
                  bgcolor="transparent"
                />
              ) : (
                <IconButton.Back
                  onClick={() => router?.goBack(defaultBackPage)}
                  size="medium"
                  color={color}
                  bgcolor="transparent"
                  sx={{
                    ml: '-8px',
                  }}
                />
              ))}
            {left}
          </Stack>
          <Stack flex={6} sx={{ minWidth: 0 }}>
            {typeof title === 'string' ? (
              <Typography
                variant="h5"
                sx={styleSets.lineCamp(1)}
                align={leftTitle ? 'left' : 'center'}
                color={color ? 'white' : 'text.primary'}
              >
                {title}
              </Typography>
            ) : (
              title
            )}
          </Stack>
          <Stack flex={1} direction="row" justifyContent="flex-end">
            {right}
          </Stack>
        </Toolbar>
      </AppBar>
    )
  }
)
